import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type ITabItem } from 'shared/ui'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import {
  type IAgentFirstStepPayload,
  type IChatbotFullData,
  type IChatbotMessage,
} from 'entities/Chatbot'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  EmojiAction,
  EnumVariantMessageField,
  SavedRepliesAction,
  ShortenLinkAction,
} from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { MessageFieldStore } from 'widgets/MessageField/store/messageFieldStore'
import { mergeFieldReplacer } from 'widgets/MessageField/lib'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { SavedRepliesStore } from 'widgets/SavedReplies'
import type { AgentBookAppointmentStepsStore } from 'pages/chatbot/pages/agentBookAppointment/store/agentBookAppointmentStepsStore'
import { type IHandleError } from 'pages/chatbot/pages/agentBookAppointment/store/type'

export class SettingAgentStore {
  private _conversationType: IChatbotMessage['type'] = 'auto'
  private _messageFieldStore: MessageFieldStore
  private readonly _stepsStore: AgentBookAppointmentStepsStore
  private _errorsMap: Map<string, string> = new Map()
  private _disposeFieldReaction: IReactionDisposer | null = null
  private _savedRepliesStore: SavedRepliesStore
  private _message = ''
  private _attachments: Attachment[] = []
  private _loading = false

  constructor(stepsStore: AgentBookAppointmentStepsStore, private _entities: IEntitiesStore) {
    this._stepsStore = stepsStore

    this._savedRepliesStore = new SavedRepliesStore(this._entities)
    this._messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Write your message...',
        showActionsItems: 5,
        styles: {
          minHeight: 64,
        },
        variant: EnumVariantMessageField.Default,
        resetOnUnmount: false,
        makeActions: () => [
          { iconButtonComponent: <MediaAction /> },
          { iconButtonComponent: <SavedRepliesAction store={this._savedRepliesStore} useHotKey /> },
          { iconButtonComponent: <EmojiAction /> },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this._handleMergeField(value)}
                isAllIntegration
              />
            ),
          },
          { iconButtonComponent: <ShortenLinkAction /> },
        ],
        makeEvents: () => ({
          onFocus: () => {
            this.removeError('first_message.message')
          },
        }),
      },
      this._entities
    )

    makeAutoObservable(this)
    this.reactionFieldChange()
  }

  reactionFieldChange = () => {
    this._disposeFieldReaction?.()
    this._disposeFieldReaction = reaction(
      () => ({
        text: this._messageFieldStore.text,
        attachments: this._messageFieldStore.attachments,
      }),
      ({ text, attachments }) => {
        const prevAttachments = this._attachments
        const attachmentsAreSame =
          prevAttachments.length === attachments.length &&
          prevAttachments.every((attachment) => attachments.includes(attachment))

        const prevText = this._message
        const textIsSame = prevText === text

        if (attachmentsAreSame && textIsSame) return

        this.syncMessageInfo(text, attachments)
      }
    )
  }

  get disabled() {
    return this._loading
  }

  get isAutoConvType() {
    return this._conversationType === 'auto'
  }

  get tabs(): ITabItem<'auto' | 'manual'>[] {
    return [
      {
        key: 'auto',
        name: 'AI generated',
      },
      {
        key: 'manual',
        name: 'Custom',
      },
    ]
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get conversationType() {
    return this._conversationType
  }

  get errorsMap() {
    return this._errorsMap
  }

  get stepPayload(): IAgentFirstStepPayload {
    return {
      name: this._stepsStore.chatbot.name,
      first_message: {
        type: this._conversationType,
        message: this._message,
        media_url: this._attachments.map((attachment) => new RequestAttachment(attachment)),
      },
      settings: {
        use_knowledge_base: {
          value: this._stepsStore.chatbot.settings.knowledgeBaseUsage,
        },
      },
      knowledge_base_documents: this._stepsStore.chatbot.knowledgeBaseDocuments,
    }
  }

  syncMessageInfo(message: string, attachments: Attachment[]) {
    this._message = message
    this._attachments = attachments
  }

  init = async (data: IChatbotFullData | null) => {
    if (!data) return

    const { message, media_url, type } = data.first_message ?? {}

    this._message = message ?? ''
    this._attachments = media_url?.map((item) => new Attachment({ requestAttachment: item })) ?? []
    this._conversationType = type ?? 'manual'

    this._messageFieldStore.setMessageData({
      message: this._message,
      attachments: this._attachments,
      isReset: false,
      isReplace: true,
    })
  }

  setConversationType = (value: IChatbotMessage['type']) => {
    this._conversationType = value

    this.removeError('first_message.message')
  }

  private _handleMergeField = (field: MergeField) => {
    if (!this._messageFieldStore.addContent) return

    const fieldContent = mergeFieldReplacer(this._entities, field.template) + '&nbsp;'

    this._messageFieldStore.addContent(fieldContent)
  }

  setError = (key: string, error: string) => {
    if (key === 'first_message.message') {
      this._messageFieldStore.setHighlight('error')
    }
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    if (key === 'first_message.message') {
      this._messageFieldStore.clearHighlight()
    }
    this._errorsMap.delete(key)
  }

  titleValidate = () => {
    if (!this._stepsStore.chatbot.name) {
      this._stepsStore.chatbotErrors.sync({ name: ['Please add a name for your AI Agent.'] })
      return false
    }
    return true
  }

  checkValidate = () => {
    let validate = this.titleValidate()

    if (!this.isAutoConvType) {
      if (!this._messageFieldStore.messageText) {
        this.setError(
          'first_message.message',
          'Please provide the initial message for the agent to send.'
        )
        validate = false
      }
    }

    return validate
  }

  handleError = (errors: IHandleError) => {
    if (errors?.name) {
      this._stepsStore.chatbotErrors.sync({
        name: Array.isArray(errors.name) ? errors.name : [errors.name],
      })
    } else {
      Object.entries(errors)?.forEach(([errorKey, errorMessages]) => {
        this.setError(errorKey, Array.isArray(errorMessages) ? errorMessages[0] : errorMessages)
      })
    }
  }

  handleSaveDraft = async (id?: string, shouldRedirect = true) => {
    if (!this.titleValidate()) return false
    if (this._loading) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      await this._stepsStore.quickSaveDraft('step_1', { shouldRedirect, id: Number(id) })
      return true
    } catch (e) {
      this._errorsMap.clear()
      this._stepsStore.chatbotErrors.clear()

      this._stepsStore.handleApiError(e, (errors) => this.handleError(errors))
      return false
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleNextClick = async (id?: string) => {
    if (!this.checkValidate()) return

    if (this._loading) return false

    runInAction(() => {
      this._loading = true
    })

    try {
      await this._stepsStore.handleStepValidationAndNavigation('step_1', this.stepPayload, id)
    } catch (e) {
      this._stepsStore.handleValidationApiError(e, this.handleError)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  dispose = () => {
    this._disposeFieldReaction?.()
    this._messageFieldStore.reset()
  }
}
